'use client';

/**
 * BrandIcons — true brand logomarks for the landing lead-flow constellation.
 *
 * Owner request 2026-06-23: use the REAL brand icons for the lead sources, not the
 * monochrome lucide proxies (`Music2`/`MessageCircle`/`Mail`) the integration strip
 * uses. Each mark is the official single-path logo (shape sourced from simple-icons)
 * rendered in the brand's own colour, so it reads on a light/white node — matching
 * the referenced node-graph aesthetic.
 *
 * Brand/trademark note: this is nominative "works-with" use; colours follow each
 * brand's public mark. "Website" is a category (not a brand) → a neutral globe.
 *
 * Related: [[knowledge/ui-components]] · used by LeadFlowDiagram.tsx
 */

import type { CSSProperties } from 'react';
import { Globe, Mail } from 'lucide-react';

const META_PATH =
  'M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z';

const TIKTOK_PATH =
  'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z';

const WHATSAPP_PATH =
  'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z';

const GMAIL_PATH =
  'M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z';

// Each brand rendered in its own colour so it reads on a white node.
const BRANDS: Record<string, { path: string; color: string; label: string }> = {
  Meta: { path: META_PATH, color: '#0866FF', label: 'Meta' },
  TikTok: { path: TIKTOK_PATH, color: '#161823', label: 'TikTok' },
  WhatsApp: { path: WHATSAPP_PATH, color: '#25D366', label: 'WhatsApp' },
  Gmail: { path: GMAIL_PATH, color: '#EA4335', label: 'Gmail' },
};

/**
 * Resolve a lead-source name to its true brand mark. Brands render their official
 * single-path logo in brand colour; "Website" (a category, not a brand) falls back
 * to a neutral slate globe.
 */
export function SourceBrandIcon({ name, className }: { name: string; className?: string }) {
  const brand = BRANDS[name];
  if (brand) {
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        fill={brand.color}
        role="img"
        aria-label={brand.label}
      >
        <path d={brand.path} />
      </svg>
    );
  }
  // Website / unknown → neutral globe (slate-500).
  return <Globe className={className} color="#64748b" aria-hidden="true" />;
}

// Same single-path shapes as SourceBrandIcon, resolved by a case-insensitive
// substring match against the lead-source label so both "Meta" and "Meta Ads"
// (or the Romanian "Reclame Meta") hit the same brand.
const MONO_PATH_BRANDS: { match: string; path: string }[] = [
  { match: 'meta', path: META_PATH },
  { match: 'tiktok', path: TIKTOK_PATH },
  { match: 'gmail', path: GMAIL_PATH },
];

/**
 * SourceGlyphMono — monochrome (`currentColor`) lead-source glyph for FILLED
 * mini product mocks (r3.5 figure-specs §0 colour law: source glyphs render
 * MONOCHROME `DARK_INK.oneOff` (`#A3A09B` post-R6 warm retint, was `#98A1AE`),
 * never the brand blue/red `SourceBrandIcon` uses — that component stays
 * reserved for the lead-flow constellation's white nodes). This component
 * renders `currentColor` (no literal hex of its own) — the call site supplies
 * the actual color via its own `className`/`style`; per the R6 contract, any
 * call site applying the one-off ink here should import `DARK_INK.oneOff.text`
 * from `ui/dark-tokens.ts` rather than hand-typing the hex.
 * Website/Email have no brand mark to desaturate, so they fall back to neutral
 * lucide glyphs (Globe / Mail) rather than borrowing an unrelated brand path.
 */
export function SourceGlyphMono({
  name,
  className,
  style,
}: {
  name: string;
  className?: string;
  style?: CSSProperties;
}) {
  const lower = name.toLowerCase();
  const brand = MONO_PATH_BRANDS.find((b) => lower.includes(b.match));
  if (brand) {
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        style={style}
        fill="currentColor"
        aria-hidden="true"
      >
        <path d={brand.path} />
      </svg>
    );
  }
  if (lower.includes('email') || lower.includes('outlook') || lower.includes('mail')) {
    return <Mail className={className} style={style} aria-hidden="true" />;
  }
  // "Website"/"Site și formulare" and anything unrecognized → neutral globe.
  return <Globe className={className} style={style} aria-hidden="true" />;
}
